/**
 * ComparisonDescentsGrid
 *
 * Shows common descent comparison where both runners' times are measured over
 * runner A's descent segments (boundaries). Computed by the backend via
 * compute_common_descents() which uses A's descents as authoritative.
 *
 * Double-click a row → onDescentFocus(startM, endM) → parent zooms GapChart.
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

// Descent category colors (blue-to-violet gradient from RunningDescendsGrid)
const CATEGORY_COLORS = {
  'R-D4':  '#8ec5f5',  // sky blue
  'R-D3':  '#6898e8',  // cornflower blue
  'R-D2':  '#6b6ee0',  // blue-violet
  'R-D1':  '#8c48cc',  // indigo-violet
  'R-DHC': '#be35b8',  // vivid violet
};

const CATEGORY_TEXT = {
  'R-D4':  '#002a3a',
  'R-D3':  '#fff',
  'R-D2':  '#fff',
  'R-D1':  '#fff',
  'R-DHC': '#fff',
};

// Twistiness colors (traffic-light scale from RunningDescendsGrid)
const TWISTINESS_COLORS = {
  'Straight':  '#27AE60',
  'Flowy':     '#4D7C0F',
  'Twisty':    '#9A7D0A',
  'Hairpins':  '#D35400',
  'Technical': '#C0392B',
};

function formatDuration(s) {
  if (s == null) return '—';
  const m = Math.floor(Math.abs(s) / 60);
  const sec = Math.floor(Math.abs(s) % 60);
  const sign = s < 0 ? '−' : '';
  return `${sign}${m}:${sec.toString().padStart(2, '0')}`;
}

function formatPace(v) {
  if (v == null) return '—';
  const m = Math.floor(v);
  const s = Math.round((v - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function formatHr(v) {
  return v != null ? `${Math.round(v)} bpm` : '—';
}

function CategoryChip({ category, colors }) {
  const bgColor = CATEGORY_COLORS[category] || colors.grey[400];
  const textColor = CATEGORY_TEXT[category] || '#fff';
  return (
    <Chip
      label={category || '—'}
      size="small"
      sx={{ backgroundColor: bgColor, color: textColor, fontWeight: 'bold', fontSize: '0.65rem', height: 20 }}
    />
  );
}

function TwistinessChip({ twistinessLabel }) {
  const bgColor = TWISTINESS_COLORS[twistinessLabel] || '#64748b';
  return (
    <Chip
      label={twistinessLabel || '—'}
      size="small"
      sx={{ backgroundColor: bgColor, color: '#fff', fontWeight: 'bold', fontSize: '0.65rem', height: 20 }}
    />
  );
}

function DeltaCell({ value, lowerIsBetter = true }) {
  if (value == null) return <TableCell align="center" sx={{ color: 'inherit' }}>—</TableCell>;
  // Colour shows WHO WON (blue = A, orange = B); the number is just the margin, with
  // no +/- sign — a "+10" looked like A was slower when it actually meant A won by 10.
  const bWon = lowerIsBetter ? value < -5 : value > 5;
  const aWon = lowerIsBetter ? value > 5 : value < -5;
  const color = aWon ? COLOR_A : bWon ? COLOR_B : undefined;
  return (
    <TableCell align="center" sx={{ color, fontWeight: 'bold' }}>
      {formatDuration(Math.abs(value))}
    </TableCell>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Common Descents (primary, backend-computed)
// ---------------------------------------------------------------------------

function CommonDescentsTable({ commonDescents, onDescentFocus, selectedDescent, onDescentSelect }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [sortBy, setSortBy] = useState('start');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    return [...commonDescents].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'delta') {
        valA = Math.abs(a.time_delta_s ?? 0);
        valB = Math.abs(b.time_delta_s ?? 0);
      } else {
        valA = a.start_distance_m ?? 0;
        valB = b.start_distance_m ?? 0;
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [commonDescents, sortBy, sortDir]);

  const handleSortBy = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'start' ? 'asc' : 'desc');
    }
  };

  return (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 80 }}>Cat</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 90 }}>Twistiness</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 70 }}>
              <TableSortLabel
                active={sortBy === 'start'}
                direction={sortBy === 'start' ? sortDir : 'asc'}
                onClick={() => handleSortBy('start')}
                sx={{ color: 'inherit !important' }}
              >
                Start km
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 70 }}>Length</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 60 }}>Loss</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>A Time</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>B Time</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold' }}>
              <TableSortLabel
                active={sortBy === 'delta'}
                direction={sortBy === 'delta' ? sortDir : 'desc'}
                onClick={() => handleSortBy('delta')}
                sx={{ color: 'inherit !important' }}
              >
                Δ Time
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>A Pace</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>B Pace</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>A HR</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>B HR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((d, i) => {
            const startKm = d.start_distance_m != null ? `${(d.start_distance_m / 1000).toFixed(1)}` : '—';
            const lengthKm = d.length_m != null ? `${(d.length_m / 1000).toFixed(2)} km` : '—';
            const loss = d.elevation_loss_m != null ? `${Math.round(d.elevation_loss_m)} m` : '—';
            const isSelected =
              selectedDescent != null &&
              Math.abs((d.start_distance_m ?? -1) - selectedDescent.start_distance_m) < 1;
            return (
              <TableRow
                key={i}
                hover
                onClick={() => {
                  if (onDescentSelect && d.start_distance_m != null && d.end_distance_m != null) {
                    onDescentSelect({ start_distance_m: d.start_distance_m, end_distance_m: d.end_distance_m });
                  }
                }}
                onDoubleClick={() => {
                  if (onDescentFocus && d.start_distance_m != null && d.end_distance_m != null) {
                    onDescentFocus(d.start_distance_m, d.end_distance_m);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? `${colors.blueAccent[900]} !important` : undefined,
                  outline: isSelected ? `2px solid ${colors.blueAccent[500]}` : undefined,
                  outlineOffset: '-2px',
                  '&:nth-of-type(even)': { backgroundColor: isSelected ? undefined : colors.grey[800] },
                  '&:hover': { backgroundColor: isSelected ? `${colors.blueAccent[800]} !important` : colors.grey[700] },
                }}
              >
                <TableCell><CategoryChip category={d.category} colors={colors} /></TableCell>
                <TableCell><TwistinessChip twistinessLabel={d.twistiness_label} /></TableCell>
                <TableCell sx={{ color: colors.grey[300] }}>km {startKm}</TableCell>
                <TableCell sx={{ color: colors.grey[400], fontSize: '0.75rem' }}>{lengthKm}</TableCell>
                <TableCell sx={{ color: colors.grey[400], fontSize: '0.75rem' }}>{loss}</TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>{formatDuration(d.a?.duration_s)}</TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>{formatDuration(d.b?.duration_s)}</TableCell>
                <DeltaCell value={d.time_delta_s} lowerIsBetter={true} />
                <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>{formatPace(d.a?.avg_pace)}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>{formatPace(d.b?.avg_pace)}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>{formatHr(d.a?.avg_hr)}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>{formatHr(d.b?.avg_hr)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ComparisonDescentsGrid({
  descentsA = [],
  descentsB = [],
  commonDescents = null,
  onDescentFocus,
  selectedDescent = null,
  onDescentSelect = null,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const hasCommon = commonDescents && commonDescents.length > 0;
  const hasAny = descentsA.length > 0 || descentsB.length > 0;

  if (!hasCommon && !hasAny) {
    return (
      <Typography variant="caption" color={colors.grey[500]}>
        No descents detected in either workout.
      </Typography>
    );
  }

  return (
    <Box>
      {hasCommon ? (
        <>
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
            Descent Comparison
          </Typography>
          <Typography variant="caption" color={colors.grey[400]} display="block" mb={1}>
            Both runners measured over runner A's descent segments.
            Double-click a row to zoom Race Dynamics chart to that descent.
          </Typography>
          <CommonDescentsTable
            commonDescents={commonDescents}
            onDescentFocus={onDescentFocus}
            selectedDescent={selectedDescent}
            onDescentSelect={onDescentSelect}
          />
        </>
      ) : (
        <Box>
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1}>
            Descent Comparison Unavailable
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mb={1}>
            Fair segment comparison not available because workout A doesn't have descents analyzed.
          </Typography>
          <Typography variant="caption" color={colors.grey[500]} display="block" mb={2}>
            This usually means the workout was analyzed before descent detection was available.
            Try re-uploading or re-analyzing workout A to enable descent comparison.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
